export default {
    title: "Document Management",
    subtitle: "Configure document types, schemas, attributes and training for the AI agent",
    tabs: {
        types: "Document Types",
        attributes: "Document Attributes",
        connections: "Doc Connection Manager",
        requirements: "Requirements",
        training: "Training",
    },
    types: {
        title: "Document Types",
        description: "Manage document types available in the system",
        newDocumentType: "New Document Type",
        filters: {
            keywordPlaceholder: "Search by keyword...",
            attributePlaceholder: "Filter by specific attribute",
            allAttributes: "All attributes",
            groupBy: "Group by",
            groupByNone: "None",
            groupByPhase: "Phase",
            groupByCategory: "Category",
            ungrouped: "Ungrouped",
            expandAll: "Expand All",
            collapseAll: "Collapse All",
        },
        headers: {
            key: "Key",
            label: "Label",
            description: "Description",
            phase: "Phase",
            category: "Category",
            schemasCount: "Schemas Count",
            attributesCount: "Attributes Count",
            createdAt: "Created At",
            actions: "Actions",
        },
        modal: {
            new: "New Document Type",
            edit: "Edit Document Type",
            fields: {
                key: "Key",
                keyPlaceholder: "e.g.: bill_of_lading",
                label: "Label",
                labelPlaceholder: "e.g.: Bill of Lading",
                description: "Description",
                descriptionPlaceholder: "Document type description",
                phase: "Phase",
                phasePlaceholder: "e.g.: SHIPPING PHASE",
                category: "Category",
                categoryPlaceholder: "e.g.: Transport Documents",
            },
        },
        actions: {
            edit: "Edit",
            delete: "Delete",
            viewSchemas: "Schemas",
            viewAttributes: "Attributes",
        },
    },
    schemas: {
        title: "Document Schemas",
        description: "Manage schemas for each document type with vertical variants",
        newSchema: "New Schema",
        searchPlaceholder: "Search by name, type...",
        documentTypePlaceholder: "Filter by document type",
        documentTypeSearchPlaceholder: "Search document type...",
        regionPlaceholder: "Filter by region",
        statusPlaceholder: "Filter by status",
        headers: {
            documentType: "Document Type",
            schemaName: "Schema Name",
            schemaCode: "Code",
            region: "Region",
            attributeCount: "Attributes",
            parserRulesCount: "Parser Rules",
            isDefault: "Default",
            isActive: "Status",
            createdAt: "Created At",
            actions: "Actions",
        },
        modal: {
            new: "New Schema",
            edit: "Edit Schema",
            duplicateTitle: "Duplicate schema",
            duplicateDescription: "Enter name and code for the new schema. The rest of the information and attributes will be copied from the source schema.",
            fields: {
                documentType: "Document Type",
                documentTypePlaceholder: "Select document type",
                documentTypeSearch: "Search document type...",
                schemaName: "Schema Name",
                schemaNamePlaceholder: "e.g.: WayBill China/Asia",
                schemaCode: "Schema Code",
                schemaCodePlaceholder: "e.g.: waybill_china_asia",
                version: "Version",
                versionPlaceholder: "e.g.: 1.0",
                description: "Description",
                descriptionPlaceholder: "Schema description",
                useCases: "Use Cases",
                useCasesPlaceholder: "When to use this schema",
                region: "Region",
                regionPlaceholder: "e.g.: asia, europe",
                countryCodes: "Country Codes",
                countryCodesPlaceholder: "e.g.: CN, JP, KR",
                carrierCodes: "Carrier Codes",
                carrierCodesPlaceholder: "e.g.: MAEU, MSC",
                isDefault: "Default Schema",
                isActive: "Active",
            },
        },
        actions: {
            edit: "Edit",
            delete: "Delete",
            duplicate: "Duplicate",
            viewAttributes: "Attributes",
            parserRules: "Parser Rules",
        },
        status: {
            active: "Active",
            inactive: "Inactive",
            default: "Default",
        },
        messages: {
            loadError: "Error loading schemas",
            createSuccess: "Schema created successfully",
            updateSuccess: "Schema updated successfully",
            saveError: "Error saving schema",
            deleteSuccess: "Schema deleted successfully",
            deleteError: "Error deleting schema",
            saving: "Saving...",
            duplicateSuccess: "Schema duplicated successfully",
            duplicateError: "Error duplicating schema",
            duplicateValidation: "Enter name and code for the new schema",
            duplicating: "Duplicating...",
        },
    },
    schemaAttributes: {
        title: "Schema Attributes",
        description: "Manage attributes for this schema",
        backToSchemas: "Back to Schemas",
        newAttribute: "Add Attribute",
        searchPlaceholder: "Search attribute...",
        headers: {
            attributeKey: "Key",
            label: "Label",
            type: "Type",
            importance: "Importance",
            required: "Required",
            enumValues: "Enum Values",
            metadata: "Metadata",
            actions: "Actions",
        },
        modal: {
            new: "Add Attribute",
            edit: "Edit Attribute",
            createNewAttribute: "Create New Attribute",
            useExistingAttribute: "Use Existing Attribute",
            fields: {
                attribute: "Attribute",
                attributePlaceholder: "Select existing attribute",
                importanceLevel: "Importance Level",
                importanceLevelPlaceholder: "Select level",
                importanceLevels: {
                    error: "Error if missing",
                    warning: "Warning if missing",
                    optional: "Optional",
                },
                isRequired: "Required",
                displayOrder: "Display Order",
                customLabel: "Custom Label",
                customLabelPlaceholder: "Override label (optional)",
                customHelpText: "Custom Help Text",
                customHelpTextPlaceholder: "Override help text (optional)",
            },
        },
        importance: {
            error: "Error",
            warning: "Warning",
            optional: "Optional",
        },
        actions: {
            edit: "Edit",
            remove: "Remove",
        },
    },
    attributes: {
        title: "Global Attributes",
        description: "Manage reusable attributes for all schemas",
        newAttribute: "New Attribute",
        searchPlaceholder: "Search by key, label...",
        dataTypePlaceholder: "Filter by data type",
        categoryPlaceholder: "Filter by category",
        globalPlaceholder: "Filter by global",
        headers: {
            attributeKey: "Key",
            label: "Label",
            dataType: "Data Type",
            category: "Category",
            isGlobal: "Global",
            usageCount: "Usage",
            createdAt: "Created At",
            actions: "Actions",
        },
        modal: {
            new: "New Attribute",
            edit: "Edit Attribute",
            fields: {
                attributeKey: "Attribute Key",
                attributeKeyPlaceholder: "e.g.: shipper_name",
                label: "Label",
                labelPlaceholder: "e.g.: Shipper Name",
                description: "Description",
                descriptionPlaceholder: "Attribute description",
                dataType: "Data Type",
                dataTypePlaceholder: "Select data type",
                enumValues: "Enum Values",
                enumValuesPlaceholder: "Add values (one per line)",
                enumValuesHelp: "Only for enum type",
                metadata: "Metadata",
                metadataPlaceholder: '{"key": "value"}',
                metadataHelp: "Only for json type - Valid JSON",
                validationRules: "Validation Rules",
                validationRulesPlaceholder: '{"min": 0, "max": 100}',
                inputType: "Input Type",
                inputTypePlaceholder: "e.g.: text, number, date",
                placeholder: "Placeholder",
                placeholderPlaceholder: "Placeholder text",
                helpText: "Help Text",
                helpTextPlaceholder: "Help text for the user",
                category: "Category",
                categoryPlaceholder: "e.g.: parties, cargo, dates",
                displayOrder: "Display Order",
                isGlobal: "Global",
            },
        },
        actions: {
            edit: "Edit",
            delete: "Delete",
        },
        dataTypes: {
            string: "String",
            number: "Number",
            date: "Date",
            datetime: "Date/Time",
            boolean: "Boolean",
            array: "Array",
            object: "Object",
            email: "Email",
            url: "URL",
            phone: "Phone",
        },
    },
    connections: {
        title: "Doc Connection Manager",
        description: "Link attributes from different document type schemas to facilitate cross-document validation",
        newConnection: "New Connection",
        searchPlaceholder: "Search connection...",
        headers: {
            source: "Source",
            target: "Target",
            validationRule: "Validation Rule",
            created: "Created",
            actions: "Actions",
        },
        modal: {
            new: "New Connection",
            edit: "Edit Connection",
            source: "Source",
            target: "Target",
            fields: {
                sourceDocumentType: "Source Document Type",
                sourceSchema: "Source Schema",
                sourceAttribute: "Source Attribute",
                targetDocumentType: "Target Document Type",
                targetSchema: "Target Schema",
                targetAttribute: "Target Attribute",
                validationRule: "Validation Rule",
            },
        },
        validationRules: {
            mustMatch: "Must Match",
            mustBeDifferent: "Must Be Different",
            sourceContainsTarget: "Source Contains Target",
            targetContainsSource: "Target Contains Source",
            mustReference: "Must Reference",
            mustBeSimilar: "Must Be Similar",
        },
        actions: {
            edit: "Edit",
            delete: "Delete",
        },
    },
    training: {
        title: "Training Documents",
        description: "Upload documents for AI agent training",
        uploadDocuments: "Upload Documents",
        uploadArea: "Drag files here or click to select",
        supportedFormats: "Supported formats: PDF, PNG, JPG, XLSX, XLS, CSV",
        searchPlaceholder: "Search document...",
        documentTypePlaceholder: "Filter by type",
        statusPlaceholder: "Filter by status",
        headers: {
            fileName: "File Name",
            documentType: "Type",
            status: "Status",
            uploadDate: "Upload Date",
            extractionScore: "Extraction Score",
            validationScore: "Validation Score",
            detectedSchema: "Detected Schema",
            actions: "Actions",
        },
        status: {
            pending: "Pending",
            processing: "Processing",
            completed: "Completed",
            failed: "Failed",
        },
        validationStatus: {
            correct: "Correct",
            incorrect: "Incorrect",
            pending: "To Validate",
        },
        actions: {
            viewDetails: "Details",
            delete: "Delete",
            correct: "Correct",
            incorrect: "Incorrect",
            loading: "Loading...",
        },
        breadcrumb: {
            trainingDocuments: "Training Documents",
        },
        detail: {
            title: "Training Process Details",
            backToList: "Back to List",
            documentTitle: "Document",
            extractedDataTitle: "Extracted Data by Provider",
            infoTitle: "Process and Training Info",
            notFound: {
                title: "Document not found",
                message: "The requested training document does not exist.",
                backToList: "Back to List",
            },
            infoFields: {
                documentType: "Document Type",
                status: "Status",
                dataset: "Dataset",
                datasetVersion: "Dataset Version",
                schema: "Schema",
                detectedSchema: "Detected Schema",
                extractionScore: "Extraction Score",
                validationScore: "Validation Score",
                createdAt: "Creation Date",
                updatedAt: "Last Update",
                aiProviders: "AI Providers Used",
                customVariables: "Custom Variables",
                notes: "Notes",
            },
            tableHeaders: {
                attribute: "Attribute",
                extractedValue: "Extracted Value",
                confidence: "Confidence",
                validationStatus: "Validation Status",
                actions: "Actions",
            },
            actions: {
                title: "Document Actions",
            },
            extraction: {
                title: "Run Extraction",
                strategy: "Strategy",
                run: "Run Extraction",
                running: "Extracting...",
                success: "Extraction completed successfully",
                error: "Error during extraction",
                results: "Extraction Results",
                confidence: "Confidence",
                duration: "Duration",
                cost: "Cost",
                tokens: "Tokens",
                selectAsBest: "Select as Best",
                selected: "Extraction selected as best",
                selectError: "Error selecting extraction",
                noExtractions: "No extractions yet. Run an extraction to see results.",
                ocrOutput: "OCR Output",
                extractedFields: "Extracted Fields",
            },
            classification: {
                run: "Run Classification",
                running: "Classifying...",
                success: "Classification completed: {{type}} ({{confidence}}%)",
                error: "Error during classification",
                description: "Automatically detect the document type",
                result: "Classification Result",
                detectedType: "Detected Type",
            },
            validation: {
                approve: "Approve",
                reject: "Reject",
                success: "Document status updated to {{status}}",
                error: "Error updating document status",
            },
            jobHistory: {
                title: "Job History",
                noJobs: "No jobs yet. Run an extraction or classification to see job history.",
                loadError: "Error loading job history.",
                headers: {
                    type: "Type",
                    strategy: "Strategy",
                    status: "Status",
                    confidence: "Confidence",
                    duration: "Duration",
                    date: "Date",
                },
                status: {
                    pending: "Pending",
                    processing: "Processing",
                    completed: "Completed",
                    failed: "Failed",
                },
                type: {
                    parsing: "Extraction",
                    extraction: "Extraction",
                    classification: "Classification",
                    ocr: "OCR",
                },
                details: {
                    model: "Model",
                    tokens: "Tokens",
                    cost: "Cost",
                    completedAt: "Completed At",
                    error: "Error",
                },
            },
            fields: {
                headers: {
                    field: "Field",
                    value: "Value",
                    confidence: "Confidence",
                    validation: "Validation",
                },
                correct: "Correct",
                corrected: "Corrected",
                incorrect: "Incorrect",
                edit: "Edit",
                markCorrect: "Mark as correct",
                markIncorrect: "Mark as incorrect",
                enterValue: "Enter value...",
                enterJson: "Enter JSON...",
                updateSuccess: "Field updated successfully",
                updateError: "Error updating field",
                markedCorrect: "Field marked as correct",
                markedIncorrect: "Field marked as incorrect",
                validateError: "Error validating field",
                loadError: "Error loading fields",
                noFields: "No extracted fields",
                array: "Array",
                object: "Object",
            },
        },
        modal: {
            details: "Processing Details",
            fields: {
                fileName: "File Name",
                documentType: "Document Type",
                datasetName: "Dataset Name",
                datasetVersion: "Dataset Version",
                status: "Status",
                extractionScore: "Extraction Score",
                validationScore: "Validation Score",
                detectedSchema: "Detected Schema",
                processingTime: "Processing Time",
                extractedFields: "Extracted Fields",
                validationIssues: "Validation Issues",
            },
        },
        upload: {
            success: "Documents uploaded successfully",
            error: "Upload error",
            processing: "Processing...",
        },
        batchOcr: {
            selected: "{{count}} selected",
            runOcr: "Run OCR",
            clearSelection: "Clear selection",
            progressTitle: "Batch OCR",
            status: {
                submitted: "Submitted",
                processing: "Processing...",
                completed: "Completed",
                partiallyCompleted: "Partial",
                failed: "Failed",
            },
            failedCount: "{{count}} documents failed",
            messages: {
                submitted: "Batch OCR started for {{count}} documents",
                submitError: "Error starting batch OCR",
                completed: "Batch OCR completed successfully",
                partiallyCompleted: "Batch OCR completed with errors",
                failed: "Batch OCR failed",
            },
        },
        batchActions: {
            runParsing: "Run Parsing",
            runClassification: "Classify",
            delete: "Delete",
            strategyModal: {
                title: "Select Parsing Strategy",
                description: "Choose the extraction strategy to use for {{count}} documents.",
                strategyLabel: "Strategy",
            },
            deleteModal: {
                title: "Confirm Deletion",
                description: "Are you sure you want to delete {{count}} documents? This action cannot be undone.",
            },
            messages: {
                parsingSubmitted: "Parsing started for {{count}} documents",
                parsingError: "Error starting batch parsing",
                classificationSubmitted: "Classification started for {{count}} documents",
                classificationError: "Error starting batch classification",
                deleteSuccess: "{{count}} documents deleted",
                deleteError: "Error deleting documents",
            },
        },
    },
    parserRules: {
        title: "Parser Rules",
        description: "Manage Natural Language rules for the Parser agent",
        status: {
            active: "Active",
            inactive: "Inactive",
        },
        priority: "Priority",
        actions: {
            add: "Add Rule",
            edit: "Edit",
            delete: "Delete",
            duplicate: "Duplicate",
            cancelEdit: "Cancel Edit",
            confirmDelete: {
                title: "Confirm Deletion",
                message: 'Are you sure you want to delete the rule "{{name}}"? This action cannot be undone.',
            },
            confirmDuplicate: {
                title: "Duplicate Rule",
                message: 'Are you sure you want to duplicate the rule "{{name}}"? A new rule will be created with the same configuration.',
            },
        },
        modal: {
            title: "Parser Rules",
            existingRules: "Existing Rules",
            noRules: "No rules defined for this schema",
            addRule: "Add New Rule",
            editRule: "Edit Rule",
            fields: {
                ruleName: "Rule Name",
                ruleNamePlaceholder: "e.g.: Container Code Validation",
                naturalLanguageRule: "Natural Language Rule",
                naturalLanguageRulePlaceholder: "Write the rule in natural language that the Parser agent should follow...",
                naturalLanguageRuleHelp: "Describe in natural language how the agent should treat the document once classified with this schema",
                description: "Description",
                descriptionPlaceholder: "Optional rule description",
                priority: "Priority",
                isActive: "Active",
            },
        },
    },
    modal: {
        cancel: "Cancel",
        save: "Save",
        create: "Create",
        delete: "Delete",
        close: "Close",
        confirmDelete: "Confirm Deletion",
        confirmDeleteMessage: "Are you sure you want to delete this item? This action cannot be undone.",
    },
    loading: "Loading...",
    noData: {
        title: "No data available",
        message: "There are no items to display",
        createFirst: "Create the first item",
    },
    breadcrumb: {
        documentTypes: "Document Types",
        schemas: "Schemas",
        attributes: "Attributes",
    },
    common: {
        yes: "Yes",
        no: "No",
        selectFile: "Select File",
        selectedFiles: "Selected files:",
        upload: "Upload",
        selectAttribute: "Select an attribute",
        selectAtLeastOneFile: "Select at least one file",
        insertAliasPerLine: "Insert one alias per line",
        invalidJson: "Invalid JSON",
        global: "Global",
        specific: "Specific",
    },
};
